import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '../../hooks/useUserContext';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import WorkOutlined from '@mui/icons-material/WorkOutlined';
import CpaxLogo from '../../components/CpaxLogo';

const steps = ['Background', 'Confirmation'];

interface WorkEntry {
  companyName: string;
  jobTitle: string;
  industry: string;
  startMonth: string;
  startYear: string;
  isCurrent: boolean;
  endMonth: string;
  endYear: string;
  description: string;
}

interface ProfileData {
  firstName?: string;
  lastName?: string;
}

/** Format month + year pair into "MMM 'YY" style. e.g. "Aug '24" */
function formatMonthYear(month: string, year: string): string {
  if (!month || !year) return '';
  return `${month.slice(0, 3)} '${year.slice(-2)}`;
}

/** Build a date-range string like "Aug '24 – Present" or "Jan '22 – Mar '24" */
function formatDateRange(entry: WorkEntry): string {
  const start = formatMonthYear(entry.startMonth, entry.startYear);
  if (!start) return '';
  if (entry.isCurrent) return `${start} – Present`;
  const end = formatMonthYear(entry.endMonth, entry.endYear);
  return end ? `${start} – ${end}` : start;
}

/**
 * Stacked label-above-value field matching Figma's caption/body1 pattern.
 * Label: 12px, rgba(0,0,0,0.38). Value: 16px body1.
 */
function FieldItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: 1.66,
          letterSpacing: '0.4px',
          color: 'rgba(0,0,0,0.38)',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontSize: 16,
          lineHeight: 1.5,
          letterSpacing: '0.15px',
          color: 'text.primary',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

/** Card container: primary-blue header row + divider + padded items list */
function SectionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ width: '100%', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 2,
        }}
      >
        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: 20,
            fontWeight: 500,
            lineHeight: 1.6,
            letterSpacing: '0.15px',
            color: 'primary.main',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Divider />
      {/* Items area: 8px horizontal, 16px vertical outer padding, 16px gap between items */}
      <Box sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </Box>
    </Paper>
  );
}

export default function IndividualBackgroundConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clerkUser } = useUserContext();

  const workEntries: WorkEntry[] = location.state?.workEntries ?? [];
  const planData = location.state?.planData;
  const profileData: ProfileData | undefined = location.state?.profileData;

  const firstName = profileData?.firstName ?? clerkUser?.firstName ?? '';
  const lastName = profileData?.lastName ?? clerkUser?.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  const handleBack = () =>
    navigate('/auth/individual-background-manual', {
      state: { workEntries, planData, profileData },
    });

  const handleContinue = () => {
    navigate('/app/assistant', { state: { planData } });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'surface.subtle',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <CpaxLogo height={28} showMark={false} />
        </Toolbar>
      </AppBar>

      {/* Stepper AppBar — Background complete, Confirmation active */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 852 }}>
            <Stepper activeStep={1}>
              {steps.map((label, index) => (
                <Step key={label} completed={index === 0}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main scrollable content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 4, sm: 8 },
          px: { xs: 2, sm: 0 },
          pb: { xs: '80px', sm: '160px' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 608,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0,
          }}
        >
          {/* Page heading */}
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 32 },
              fontWeight: 400,
              lineHeight: 1.235,
              letterSpacing: '0.25px',
              color: 'primary.main',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Review your information
          </Typography>

          {/* User name */}
          {fullName && (
            <Typography
              variant="body1"
              sx={{
                fontSize: 16,
                lineHeight: 1.5,
                letterSpacing: '0.15px',
                color: 'text.secondary',
                textAlign: 'center',
                mb: 4,
              }}
            >
              {fullName}
            </Typography>
          )}

          {/* Section cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
            {/* ── Profile section ── */}
            <SectionCard
              icon={<CheckCircleOutlined fontSize="small" />}
              title="Profile"
            >
              <Box
                sx={{
                  bgcolor: 'rgba(86, 151, 55, 0.08)',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <FieldItem label="First Name" value={firstName} />
                  <FieldItem label="Last Name" value={lastName} />
                </Box>
              </Box>
            </SectionCard>

            {/* ── Work History section ── */}
            <SectionCard
              icon={<WorkOutlined fontSize="small" />}
              title="Work History"
            >
              {workEntries.length === 0 ? (
                <Box sx={{ py: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No work history added.
                  </Typography>
                </Box>
              ) : (
                workEntries.map((entry, index) => {
                  const dateRange = formatDateRange(entry);
                  return (
                    <Box
                      key={index}
                      sx={{
                        bgcolor: 'rgba(86, 151, 55, 0.08)',
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      {/* Title row + Edit button */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <FieldItem label="Position" value={entry.jobTitle} />
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<EditOutlined />}
                          onClick={handleBack}
                          sx={{
                            flexShrink: 0,
                            px: '10px',
                            py: '4px',
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: '22px',
                            letterSpacing: '0.46px',
                            textTransform: 'uppercase',
                            boxShadow:
                              '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                          }}
                        >
                          Edit
                        </Button>
                      </Box>

                      {/* Company + Date(s) row */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <FieldItem label="Company" value={entry.companyName} />
                        {dateRange && (
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                lineHeight: 1.66,
                                letterSpacing: '0.4px',
                                color: 'rgba(0,0,0,0.38)',
                                mb: 0.25,
                              }}
                            >
                              Date(s)
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: 16,
                                lineHeight: 1.5,
                                letterSpacing: '0.15px',
                                color: 'text.primary',
                              }}
                            >
                              {dateRange}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Industry */}
                      {entry.industry && (
                        <Box sx={{ mt: 2 }}>
                          <FieldItem label="Industry" value={entry.industry} />
                        </Box>
                      )}

                      {/* Responsibilities & Achievements */}
                      {entry.description && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              lineHeight: 1.66,
                              letterSpacing: '0.4px',
                              color: 'rgba(0,0,0,0.38)',
                              mb: 0.25,
                            }}
                          >
                            Responsibilities &amp; Achievements
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: 16,
                              lineHeight: 1.5,
                              letterSpacing: '0.15px',
                              color: 'text.primary',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {entry.description}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </SectionCard>
          </Box>
        </Box>
      </Box>

      {/* Fixed bottom bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          height: { xs: 'auto', sm: 128 },
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 0 },
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={handleBack}
            sx={{
              px: '22px',
              py: 1,
              fontSize: '0.9375rem',
              fontWeight: 500,
              letterSpacing: '0.46px',
              borderColor: 'rgba(86, 151, 55, 0.5)',
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{
              px: '22px',
              py: 1,
              fontSize: '0.9375rem',
              fontWeight: 500,
              letterSpacing: '0.46px',
            }}
          >
            Confirm &amp; Continue
          </Button>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
